import React, { useState } from "react";
import styled from "styled-components";
import { useNavigate, useParams } from "react-router-dom";
import {
  Check,
  ChevronDown,
  ChevronLeft,
  MoreVertical,
  Play,
  Trash2,
} from "react-feather";
import {
  Banner,
  Button,
  ChangesTrigger,
  ClickOutside,
  PageWrapperTwoColumn,
  Select,
  SelectOption,
  Spinner,
} from "@cinesys/ui/components";
import MetadataImportHeader from "./MetadataImportHeader";
import { Page, SidePanel, StatusPill, WorkflowBadge } from "./ui";
import { MI_BASE, MI_INTERFACE_ID, ProfileStatus } from "./config";
import { useInterfaceAccess } from "../../hooks/useInterfaceAccess";
import InterfaceAccessNotice from "../../components/InterfaceAccessNotice";
import { DelayedSpinner } from "@cinesys/experience-ui/components";
import {
  runDisplay,
  useAssetTitle,
  useCollectionTitle,
  useImportProfile,
  useImportProfiles,
  useProfileConfig,
  useRuns,
} from "./service";
import { ImportProfile } from "./mockData";
import { SaveProvider, useSaveContext, useSaveSection } from "./saveController";
import SourcePanel from "./panels/SourcePanel";
import FieldsPanel from "./panels/FieldsPanel";
import TargetPanel from "./panels/TargetPanel";
import SchedulePanel from "./panels/SchedulePanel";
import RunsPanel from "./panels/RunsPanel";
import SourcePreviewPanel from "./panels/SourcePreviewPanel";
import RunConfirmModal from "./RunConfirmModal";
import DeleteProfileModal from "./DeleteProfileModal";

/**
 * A single Metadata Workflow — one guided page. Metadata Source → Field Mappings → Applies To are
 * always-visible titled sections in the primary column (defining WHAT the workflow does); Schedule,
 * pending Changes, Preview and Run history live in the operational rail (HOW/WHEN it runs). One
 * workflow-level lifecycle control and one Save sit in the top bar; an orientation panel explains
 * the flow. Every section registers into the unified Save, so one Save persists the whole workflow.
 */

// Re-aliases the generic @cinesys/ui tokens onto the --mi (blue) palette so the shared Select /
// Input / Button pick up this product's look — including the focus/hover BORDER tokens (--blue060 /
// --blueBorder / --blueFocusRing), which otherwise fall back to the shared indigo and read purple.
const Shell = styled.div`
  padding: 0 22px 56px;
  max-width: 1440px;
  margin: 0 auto;

  --inputBackground: var(--miRaised);
  --inputBorder: var(--miLine2);
  --dark400: var(--miRaised);
  --dark500: var(--miLine2);
  --dark600: var(--miLine2);
  --dark700: var(--miInkFaint);
  --gray400: var(--miInk);
  --gray500: var(--miInk);
  --blue060: var(--miAccent);
  --blue100: var(--miAccentLine);
  --blue200: var(--miAccent);
  --blue300: var(--miAccentBright);
  --blue400: var(--miAccent);
  --blue500: var(--miAccent);
  --blueBorder: var(--miAccent);
  --blueBorderOverlay: var(--miAccent);
  --blueFocusRing: var(--miAccentSoft);
  --blueBackground: var(--miAccentSoft, var(--miRaised));
  --selectOptionHover: var(--miAccentHover);
`;

const Bar = styled.div`
  display: flex;
  align-items: center;
  gap: 12px 16px;
  flex-wrap: wrap;
  margin: 18px 0 12px;
  padding: 12px 14px;
  background: var(--miSurface);
  border: 1px solid var(--miLine);
  border-radius: 12px;
`;

const FieldLabel = styled.span`
  font-family: ui-monospace, Menlo, monospace;
  font-size: 11px;
  letter-spacing: 0.13em;
  text-transform: uppercase;
  color: var(--miInkFaint);
`;

const SwitcherWrap = styled.div`
  min-width: 240px;
  max-width: 320px;
  flex: 1 1 240px;
`;

const BarActions = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-left: auto;
`;

const SaveNote = styled.span<{ $err?: boolean }>`
  font-size: 12.5px;
  color: ${(p) => (p.$err ? "var(--miDanger)" : "var(--miInkFaint)")};
  max-width: 30ch;
`;

/** Lifecycle control — the workflow state (Draft/Active/Paused) using the established colour-coded
 *  StatusPill language, opening a small menu to change it. Its edit registers into the unified Save. */
const Lifecycle = styled.div`
  position: relative;

  > .trigger {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    background: var(--miRaised);
    border: 1px solid var(--miLine2);
    border-radius: 8px;
    padding: 6px 10px;
    cursor: pointer;
    color: var(--miInkFaint);
  }
  > .trigger:hover {
    border-color: var(--miAccentLine);
  }
  > .menu {
    position: absolute;
    top: calc(100% + 6px);
    right: 0;
    z-index: 40;
    display: flex;
    flex-direction: column;
    gap: 4px;
    padding: 6px;
    background: var(--miSurface);
    border: 1px solid var(--miLine);
    border-radius: 10px;
    box-shadow: 0 14px 34px rgba(0, 0, 0, 0.5);
  }
  > .menu button {
    display: flex;
    background: none;
    border: none;
    border-radius: 6px;
    padding: 6px 10px;
    cursor: pointer;
  }
  > .menu button:hover {
    background: var(--miRaised);
  }
`;

const LIFECYCLE_STATES: ProfileStatus[] = ["active", "draft", "paused"];

function LifecycleControl({
  status: initialStatus,
}: {
  status: ProfileStatus;
}) {
  const [edit, setEdit] = useState<ProfileStatus>();
  const [open, setOpen] = useState(false);
  const status = edit ?? initialStatus;
  const dirty = edit !== undefined && edit !== initialStatus;
  useSaveSection("status", {
    order: 0,
    label: "Status",
    dirty,
    getPatch: () => ({ status: status as "draft" | "active" | "paused" }),
  });
  return (
    <ClickOutside onClickOutside={() => setOpen(false)}>
      <Lifecycle>
        <button className="trigger" onClick={() => setOpen((v) => !v)}>
          <StatusPill status={status} />
          <ChevronDown size={14} />
        </button>
        {open && (
          <div className="menu">
            {LIFECYCLE_STATES.map((s) => (
              <button
                key={s}
                onClick={() => {
                  setEdit(s);
                  setOpen(false);
                }}
              >
                <StatusPill status={s} />
              </button>
            ))}
          </div>
        )}
      </Lifecycle>
    </ClickOutside>
  );
}

/** Compact workflow summary — keeps Source / Applies To / Schedule / Last run legible at a glance. */
const Summary = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px 28px;
  padding: 12px 16px;
  background: var(--miSurface);
  border: 1px solid var(--miLine);
  border-radius: 12px;
  margin-bottom: 12px;

  .item {
    display: flex;
    flex-direction: column;
    gap: 2px;
    min-width: 0;
  }
  .k {
    font-family: ui-monospace, Menlo, monospace;
    font-size: 10.5px;
    letter-spacing: 0.11em;
    text-transform: uppercase;
    color: var(--miInkFaint);
  }
  .v {
    font-size: 13.5px;
    color: var(--miInk);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    max-width: 42ch;
  }
  .v.muted {
    color: var(--miInkFaint);
  }
`;

/** Orientation panel — the blue-identity analog of Spotter's instruction panel: what the operator
 *  is doing and how the page flows, with the four setup stages and their completion state. */
const Guide = styled.div`
  border: 1px solid var(--miAccentLine);
  border-left: 3px solid var(--miAccent);
  background: var(--miAccentSoft);
  border-radius: 12px;
  padding: 14px 16px;
  margin-bottom: 12px;

  .title {
    color: #fff;
    font-weight: 600;
    font-size: 14.5px;
  }
  .sub {
    margin-top: 3px;
    font-size: 13px;
    color: var(--miInkSoft);
    max-width: 82ch;
  }
`;

const Steps = styled.ol`
  display: flex;
  flex-wrap: wrap;
  gap: 8px 10px;
  list-style: none;
  margin: 12px 0 0;
  padding: 0;

  li {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 13px;
    color: var(--miInkSoft);
    padding: 5px 11px 5px 8px;
    border-radius: 999px;
    border: 1px solid var(--miLine);
    background: var(--miSurface);
  }
  li.done {
    color: #fff;
    border-color: var(--miAccentLine);
  }
  .num {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 19px;
    height: 19px;
    border-radius: 50%;
    font-size: 11px;
    font-weight: 700;
    color: var(--miInkFaint);
    background: var(--miRaised);
    border: 1px solid var(--miLine);
  }
  li.done .num {
    color: #052e1a;
    background: var(--miAccentBright);
    border-color: var(--miAccentBright);
  }
`;

/** An always-visible configuration section — a titled card whose header and body share one inset,
 *  so all four stages align. Not an accordion: the stage stays visibly present. */
const SectionCard = styled.section`
  background: var(--miSurface);
  border: 1px solid var(--miLine);
  border-radius: 12px;
  overflow: hidden;

  & + & {
    margin-top: 12px;
  }
  > header {
    display: flex;
    align-items: baseline;
    gap: 12px;
    padding: 14px 20px;
    border-bottom: 1px solid var(--miLine);
  }
  > header .t {
    font-size: 15px;
    font-weight: 600;
    color: #fff;
  }
  > header .s {
    font-size: 13px;
    color: var(--miInkFaint);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  > .body {
    padding: 18px 20px;
  }
`;

const Main = styled.main`
  padding: 0 0 20px;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const Aside = styled.aside`
  padding: 0 0 20px;

  @media (min-width: 1030px) {
    position: sticky;
    top: var(--miHeaderHeight, 92px);
    max-height: calc(100vh - var(--miHeaderHeight, 92px));
    overflow-y: auto;
    margin: 0 -10px;
    padding: 0 10px 20px;
  }
`;

const ChangeList = styled.ul`
  margin: 0;
  padding: 0;
  list-style: none;
  display: flex;
  flex-direction: column;
  gap: 8px;

  li {
    display: flex;
    align-items: center;
    gap: 9px;
    font-size: 13px;
    color: var(--miInkSoft);
  }
`;

const CleanNote = styled.div`
  color: var(--miInkFaint);
  font-size: 13px;
  text-align: center;
  padding: 4px 0;
`;

/** The single Save affordance (bar, top-right). */
function SaveBar() {
  const { isDirty, isSaving, save, dirtySections, result, errorSection } =
    useSaveContext();
  const n = dirtySections.length;
  // A schedule-sync failure is shown in the Schedule rail, never as a paragraph in the action row.
  const showNote =
    !!result && !isDirty && !(errorSection === "schedule" && !result.ok);
  return (
    <>
      {showNote && (
        <SaveNote $err={!result.ok}>
          {result.ok ? result.message ?? "Saved." : result.error}
        </SaveNote>
      )}
      <Button
        label={
          isSaving
            ? "Saving…"
            : n > 0
            ? `Save ${n} change${n === 1 ? "" : "s"}`
            : "Saved"
        }
        type="success"
        disabled={!isDirty || isSaving}
        onClick={save}
      />
    </>
  );
}

/** A schedule reconcile failure — surfaced in the Schedule rail (not the top action row). */
function ScheduleSyncError() {
  const { result, errorSection } = useSaveContext();
  if (!result || result.ok || errorSection !== "schedule") {
    return null;
  }
  return (
    <div style={{ marginBottom: 12 }}>
      <Banner error={result.error} />
    </div>
  );
}

function ChangesSidePanel() {
  const { dirtySections, result, errorSection } = useSaveContext();
  return (
    <SidePanel>
      <header>
        Changes
        {dirtySections.length > 0 && <span>{dirtySections.length}</span>}
      </header>
      <div className="body">
        {dirtySections.length === 0 ? (
          <CleanNote>
            {result && !result.ok ? result.error : "No pending changes."}
          </CleanNote>
        ) : (
          <ChangeList>
            {dirtySections.map((s) => (
              <li key={s.key}>
                <ChangesTrigger
                  $tone={errorSection === s.key ? "error" : "pending"}
                />
                {s.label}
              </li>
            ))}
          </ChangeList>
        )}
      </div>
    </SidePanel>
  );
}

interface WorkflowFacts {
  source: string;
  applies: string;
  schedule: string;
  scheduleContext: string;
  lastRun: string | null;
  steps: { n: number; label: string; done: boolean }[];
  complete: boolean;
}

function useWorkflowFacts(profile: ImportProfile): WorkflowFacts {
  const assetTitle = useAssetTitle(profile.source.assetId || undefined);
  const roots =
    profile.collectionTarget?.collectionIds ??
    (profile.collectionTarget?.collectionId
      ? [profile.collectionTarget.collectionId]
      : []);
  const firstColl = useCollectionTitle(roots[0] || undefined);
  const cfg = useProfileConfig(profile.id);
  const runs = useRuns(profile.id);

  const sourceName =
    assetTitle.data?.title || (profile.source.assetId ? "Source asset" : "");
  const collName = firstColl.data?.title || (roots[0] ? "Collection" : "");
  const descend = profile.collectionTarget?.includeDescendants ?? true;

  const tz = cfg.data?.schedule.timezone;
  const scheduleBase =
    profile.schedule.mode === "manual"
      ? "Manual"
      : profile.schedule.cadence === "weekly"
      ? `Weekly · ${profile.schedule.weekday ?? ""} ${
          profile.schedule.at ?? ""
        }`.trim()
      : `Daily · ${profile.schedule.at ?? ""}`.trim();
  const schedule =
    profile.schedule.mode !== "manual" && tz
      ? `${scheduleBase} · ${tz}`
      : scheduleBase;

  const latest = runs.data?.[0];
  const lastRun = latest
    ? (() => {
        const d = runDisplay({
          status: latest.status,
          outcome: latest.metadata?.outcome,
          changed: latest.metadata?.changed,
        });
        const m = latest.metadata;
        return m
          ? `${d.label} · ${m.changed ?? 0} changed · ${
              m.unchanged ?? 0
            } unchanged`
          : d.label;
      })()
    : profile.lastRun
    ? `${profile.lastRun.changed} changed of ${profile.lastRun.total}`
    : null;

  const hasSource = !!profile.source.assetId;
  const hasFields = profile.fields.length > 0;
  const hasTarget = roots.length > 0;
  const isActive = profile.status === "active";
  const steps = [
    { n: 1, label: "Metadata Source", done: hasSource },
    { n: 2, label: "Field Mappings", done: hasFields },
    { n: 3, label: "Applies To", done: hasTarget },
    { n: 4, label: "Schedule & Activate", done: isActive },
  ];

  return {
    source: sourceName
      ? `${sourceName} · ${profile.source.format.toUpperCase()}`
      : "No source selected",
    applies:
      roots.length === 0
        ? "No collection selected"
        : `${collName}${
            roots.length > 1 ? ` + ${roots.length - 1} more` : ""
          } · ${descend ? "descendants included" : "this collection only"}`,
    schedule: scheduleBase,
    scheduleContext: schedule,
    lastRun,
    steps,
    complete: steps.every((s) => s.done),
  };
}

/** Secondary workflow actions (overflow) — keeps destructive Delete out of the primary row. */
const Overflow = styled.div`
  position: relative;

  > .trigger {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 34px;
    height: 34px;
    background: var(--miRaised);
    border: 1px solid var(--miLine2);
    border-radius: 8px;
    color: var(--miInkSoft);
    cursor: pointer;
  }
  > .trigger:hover {
    border-color: var(--miAccentLine);
    color: #fff;
  }
  > .menu {
    position: absolute;
    top: calc(100% + 6px);
    right: 0;
    z-index: 40;
    min-width: 168px;
    padding: 6px;
    background: var(--miSurface);
    border: 1px solid var(--miLine);
    border-radius: 10px;
    box-shadow: 0 14px 34px rgba(0, 0, 0, 0.5);
  }
  > .menu button {
    display: flex;
    align-items: center;
    gap: 9px;
    width: 100%;
    background: none;
    border: none;
    border-radius: 6px;
    padding: 8px 10px;
    cursor: pointer;
    font: inherit;
    font-size: 13.5px;
    color: var(--miDanger);
  }
  > .menu button:hover {
    background: var(--miDangerSoft);
  }
`;

function WorkflowShell({ profile }: { profile: ImportProfile }) {
  const navigate = useNavigate();
  const list = useImportProfiles();
  const facts = useWorkflowFacts(profile);
  const [running, setRunning] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  const workflows = list.data ?? [];
  const switcherOptions: SelectOption[] = workflows.map((w) => ({
    value: w.id,
    label: w.name,
  }));

  const summaryItem = (k: string, v: string, muted?: boolean) => (
    <div className="item">
      <span className="k">{k}</span>
      <span className={muted ? "v muted" : "v"}>{v}</span>
    </div>
  );

  return (
    <>
      {running && (
        <RunConfirmModal
          profileId={profile.id}
          onClose={() => setRunning(false)}
        />
      )}
      {deleting && (
        <DeleteProfileModal
          profileId={profile.id}
          profileName={profile.name}
          onClose={() => setDeleting(false)}
          onDeleted={() => navigate(MI_BASE)}
        />
      )}
      <Bar>
        <FieldLabel>Workflow</FieldLabel>
        <SwitcherWrap>
          <Select
            options={switcherOptions}
            value={profile.id}
            isSearchable={workflows.length > 8}
            onChange={(o) => {
              const next = (o as SelectOption)?.value as string;
              if (next && next !== profile.id) {
                navigate(`${MI_BASE}/${next}`);
              }
            }}
          />
        </SwitcherWrap>
        <WorkflowBadge mode={profile.workflow} />
        <BarActions>
          <LifecycleControl status={profile.status} />
          <Button
            label="Run Now"
            icon={Play}
            type="default"
            disabled={profile.status === "draft"}
            onClick={() => setRunning(true)}
          />
          <SaveBar />
          <ClickOutside onClickOutside={() => setMenuOpen(false)}>
            <Overflow>
              <button
                type="button"
                className="trigger"
                aria-label="Workflow actions"
                onClick={() => setMenuOpen((v) => !v)}
              >
                <MoreVertical size={18} />
              </button>
              {menuOpen && (
                <div className="menu">
                  <button
                    type="button"
                    onClick={() => {
                      setMenuOpen(false);
                      setDeleting(true);
                    }}
                  >
                    <Trash2 size={15} />
                    Delete workflow
                  </button>
                </div>
              )}
            </Overflow>
          </ClickOutside>
        </BarActions>
      </Bar>

      <Summary>
        {summaryItem("Source", facts.source, !profile.source.assetId)}
        {summaryItem("Applies to", facts.applies, !facts.steps[2].done)}
        {summaryItem("Schedule", facts.scheduleContext)}
        {summaryItem(
          "Last run",
          facts.lastRun ?? "Not run yet",
          !facts.lastRun
        )}
      </Summary>

      <Guide>
        <div className="title">Set up your metadata workflow</div>
        <div className="sub">
          Choose a metadata source, map its fields to iconik, choose where it
          applies, then set the schedule in the rail and activate it.
        </div>
        <Steps>
          {facts.steps.map((s) => (
            <li key={s.n} className={s.done ? "done" : ""}>
              <span className="num">
                {s.done ? <Check size={12} strokeWidth={3} /> : s.n}
              </span>
              {s.label}
            </li>
          ))}
        </Steps>
      </Guide>

      <PageWrapperTwoColumn
        minWidth="1060px"
        padding="0"
        defaultRatio={0.63}
        stickyTop="var(--miHeaderHeight, 92px)"
      >
        <Main>
          <SectionCard>
            <header>
              <span className="t">Metadata Source</span>
              <span className="s">{facts.source}</span>
            </header>
            <div className="body">
              <SourcePanel id={profile.id} />
            </div>
          </SectionCard>
          <SectionCard>
            <header>
              <span className="t">Field Mappings</span>
              <span className="s">
                {profile.fields.length} field
                {profile.fields.length === 1 ? "" : "s"} mapped
              </span>
            </header>
            <div className="body">
              <FieldsPanel id={profile.id} />
            </div>
          </SectionCard>
          <SectionCard>
            <header>
              <span className="t">Applies To</span>
              <span className="s">{facts.applies}</span>
            </header>
            <div className="body">
              <TargetPanel id={profile.id} />
            </div>
          </SectionCard>
        </Main>
        <Aside>
          <SidePanel>
            <header>Schedule</header>
            <div className="body">
              <ScheduleSyncError />
              <SchedulePanel id={profile.id} />
            </div>
          </SidePanel>
          <ChangesSidePanel />
          <SourcePreviewPanel id={profile.id} />
          <SidePanel>
            <header>Run history</header>
            <div className="body">
              <RunsPanel id={profile.id} />
            </div>
          </SidePanel>
        </Aside>
      </PageWrapperTwoColumn>
    </>
  );
}

const ProfileScreen = () => {
  const { profileId } = useParams<{ profileId: string }>();
  const navigate = useNavigate();
  const access = useInterfaceAccess(MI_INTERFACE_ID);
  const query = useImportProfile(profileId);

  // Org-level entitlement gate — see `CandidateAssignmentsScreen.tsx`'s equivalent. Checked
  // before this screen's own query loading below.
  if (access.status !== "granted") {
    return (
      <Page>
        <MetadataImportHeader />
        {access.status === "denied" ? (
          <InterfaceAccessNotice reason={access.reason} />
        ) : (
          <DelayedSpinner padTop />
        )}
      </Page>
    );
  }

  if (query.isLoading) {
    return (
      <Page>
        <MetadataImportHeader />
        <Spinner padTop />
      </Page>
    );
  }

  const profile = query.data;
  if (query.isError || !profile) {
    return (
      <Page>
        <MetadataImportHeader />
        <Shell>
          <div style={{ padding: "18px 0" }}>
            <Banner
              error={
                query.isError
                  ? "Could not load this workflow."
                  : "Metadata workflow not found."
              }
            />
          </div>
          <Button
            label="Back to workflows"
            icon={ChevronLeft}
            type="default"
            onClick={() => navigate(MI_BASE)}
          />
        </Shell>
      </Page>
    );
  }

  return (
    <Page>
      <MetadataImportHeader crumb={profile.name} />
      <Shell>
        {/* Key by workflow id: switching workflows remounts the whole configuration subtree so every
            editor + the SaveProvider re-initialize from the NEW workflow's own authoritative config.
            This is what guarantees a workflow-A draft (mappings, source, target, dirty state) can
            never render or persist against workflow B. */}
        <SaveProvider key={profile.id} id={profile.id}>
          <WorkflowShell profile={profile} />
        </SaveProvider>
      </Shell>
    </Page>
  );
};

export default ProfileScreen;
